/* eslint-disable @typescript-eslint/no-explicit-any */
import { Check, PriorityHigh } from '@mui/icons-material';
import ArchiveIcon from '@mui/icons-material/Archive';
import FactCheck from '@mui/icons-material/FactCheck';
import { Chip, Tooltip, Typography, Zoom } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-premium';
import { baseCell, booleanCell } from 'components/cells';
import { filterOperators, dateFilterOperators, booleanFilterOperators } from 'components/enhanced';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export const InventoryCheckAlternativeColumns = (
  handleValidation: (id: number) => void,
  handleArchive: (id: number) => void
): GridColDef[] => {
  const { t } = useTranslation();
  return [
    {
      field: 'code',
      headerName: t(`global.id`),
      width: 300,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <div>
            <Link to={`${params.id}`}>
              <b className="text-secondary-500">{params.value}</b>
            </Link>
          </div>
        );
      }
    },
    {
      field: 'created_by',
      headerName: t(`global.created_by`),
      width: 150,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'date',
      headerName: t(`global.date`),
      width: 150,
      filterOperators: dateFilterOperators,
      renderCell: baseCell
    },
    {
      field: 'notes',
      headerName: t(`global.description`),
      width: 350,
      filterOperators,
      editable: true,
      renderCell: baseCell
    },
    {
      field: 'warehouse',
      headerName: t(`global.warehouse`),
      width: 350,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'partial',
      headerName: t(`global.partial`),
      width: 100,
      type: 'boolean',
      renderCell: booleanCell(t)
    },
    {
      field: 'criterion',
      headerName: t(`global.criterion`),
      width: 150,
      type: 'singleSelect',
      valueOptions: [
        { value: 'SUPPLIER', label: t('global.supplier') },
        { value: 'LOCATION', label: t('global.location') },
        { value: 'CATEGORY', label: t('global.category') }
      ],
      renderCell: (params: GridRenderCellParams<any>) => {
        return (params.value && t(`global.${params.value.toLowerCase()}`)) ?? '';
      }
    },
    {
      field: 'suppliers',
      headerName: t(`global.suppliers`),
      width: 200,
      filterOperators,
      valueGetter: (params) => params.value && params.value.join(', '),
      renderCell: baseCell
    },
    {
      field: 'locations',
      headerName: t(`global.locations`),
      width: 200,
      filterOperators,
      valueGetter: (params) => params.value && params.value.join(', '),
      renderCell: baseCell
    },
    {
      field: 'categories',
      headerName: t(`global.categories`),
      width: 200,
      valueGetter: (params) => params.value && params.value.join(', '),
      renderCell: baseCell
    },
    {
      field: 'is_valid',
      headerName: t(`global.status`),
      width: 100,
      type: 'boolean',
      filterOperators: booleanFilterOperators,
      renderCell: (params: GridRenderCellParams<any>) =>
        !params.row.open && (
          <Tooltip
            TransitionComponent={Zoom}
            arrow
            title={
              !params.row.is_valid &&
              params.row.errors.length > 0 && (
                <React.Fragment>
                  <Typography fontSize={12} color="inherit">
                    <ul>
                      {params.row.errors.map((error, i) => (
                        <li key={i}>- {error}</li>
                      ))}
                    </ul>
                  </Typography>
                </React.Fragment>
              )
            }>
            <Chip
              sx={{ background: 'transparent' }}
              icon={params.value ? <Check color="success" /> : <PriorityHigh color="warning" />}
            />
          </Tooltip>
        )
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t(`global.actions`),
      filterable: false,
      getActions: (params: GridRowParams) => {
        return [
          {
            label: 'Valida',
            icon: <FactCheck />,
            onClick: () => handleValidation(params.row.id)
          },
          {
            label: 'Archivia',
            icon: <ArchiveIcon />,
            onClick: () => handleArchive(params.row.id)
          }
        ];
      }
    } as GridColDef
  ];
};
